import { useState } from "react";
import $api from "@/zapi-frontend/shared/api/axios";
import { useCaseBriefChatStore } from "@/zapi-frontend/stores/caseBriefChatStore";
import { TDocumentStatus } from "@/zapi-frontend/shared/types/shared";

export type TPreUploadedDocument = {
  documentId: number;
  fileName: string;
  status: TDocumentStatus;
  fileSize?: number;
  createdAt?: Date;
  webUrl?: string;
};

export type TMinFile = {
  name: string;
  size: number;
  id: number;
  status: TDocumentStatus;
  isDeleting: boolean;
  isUploading: boolean;
  webUrl?: string;
};

type TPreUploadResponse = {
  data: {
    documentId: number;
    fileName: string;
    status: TDocumentStatus;
  };
};

type TPreUploadWebLinkResponse = {
  data: {
    documentId: number;
    webUrl: string;
    status: TDocumentStatus;
  };
};

// Where an upload belongs: the draft it counts against and whose chat should be able to search it.
export type TUploadTarget = {
  language?: string;
  caseBriefId?: number | null;
  vectorStoreId?: string | null;
  organizationId?: number | null;
};

// Everything one draft uploads (setup, Vault panel and chat attachments) may add up to this much.
export const DRAFT_UPLOAD_LIMIT_BYTES = 100_000_000;

const splitByDraftLimit = (files: File[], uploadedBytes: number): { accepted: File[]; rejected: File[] } => {
  let total = uploadedBytes;
  const accepted: File[] = [];
  const rejected: File[] = [];
  for (const file of files) {
    if (total + file.size <= DRAFT_UPLOAD_LIMIT_BYTES) {
      accepted.push(file);
      total += file.size;
    } else {
      rejected.push(file);
    }
  }
  return { accepted, rejected };
};

let uploadingBytes = 0;

// Splits a batch by the draft's limit, counting what the draft holds and what is still uploading.
// An accepted file counts as uploading until it is released: once it is in the draft's list, or
// has failed.
export const reserveDraftUpload = (
  files: File[]
): { accepted: File[]; rejected: File[]; release: (file: File) => void } => {
  const uploadedBytes = useCaseBriefChatStore.getState().uploadedFiles.reduce((sum, f) => sum + (f.fileSize ?? 0), 0);
  const { accepted, rejected } = splitByDraftLimit(files, uploadedBytes + uploadingBytes);
  const reserved = new Set(accepted);
  accepted.forEach((file) => (uploadingBytes += file.size));
  return {
    accepted,
    rejected,
    release: (file: File) => {
      if (reserved.delete(file)) uploadingBytes -= file.size;
    }
  };
};

// The file goes straight to storage through a short-lived URL; the server only registers it.
export const uploadDocumentFile = async (file: File, target: TUploadTarget): Promise<TPreUploadedDocument> => {
  const { data: slot } = await $api.post<{ uploadUrl: string; fileName: string }>("/projects/documents/upload-url", {
    fileName: file.name,
    fileSize: file.size,
    ...(target.caseBriefId && { caseBriefId: target.caseBriefId })
  });

  const put = await fetch(slot.uploadUrl, {
    method: "PUT",
    headers: { "x-ms-blob-type": "BlockBlob", "Content-Type": file.type || "application/octet-stream" },
    body: file
  });
  if (!put.ok) throw new Error(`Upload of ${file.name} failed with ${put.status}`);

  const response = await $api.post<TPreUploadResponse>("/projects/documents/pre-upload-complete", {
    fileName: slot.fileName,
    ...(file.type && { contentType: file.type }),
    ...(target.language && { language: target.language }),
    ...(target.caseBriefId && { caseBriefId: target.caseBriefId }),
    ...(target.vectorStoreId && { vectorStoreId: target.vectorStoreId }),
    ...(target.organizationId && { organizationId: target.organizationId })
  });
  return {
    documentId: response.data.data.documentId,
    fileName: response.data.data.fileName,
    status: response.data.data.status,
    fileSize: file.size
  };
};

export const preUploadWebLink = async (url: string, language?: string): Promise<TPreUploadedDocument> => {
  const response = await $api.post<TPreUploadWebLinkResponse>("/projects/documents/pre-upload-url", {
    url,
    language
  });
  return {
    documentId: response.data.data.documentId,
    fileName: response.data.data.webUrl,
    status: response.data.data.status,
    webUrl: response.data.data.webUrl
  };
};

export const usePreUploadDocuments = () => {
  const [uploadedDocuments, setUploadedDocuments] = useState<TPreUploadedDocument[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [pendingUploadNames, setPendingUploadNames] = useState<string[]>([]);

  const addPending = (name: string) => setPendingUploadNames((prev) => [...prev, name]);
  const removePending = (name: string) =>
    setPendingUploadNames((prev) => {
      const idx = prev.indexOf(name);
      if (idx === -1) return prev;
      return [...prev.slice(0, idx), ...prev.slice(idx + 1)];
    });

  // Each file settles on its own, so one that fails doesn't drop the others; `onSettled` runs as
  // each one lands (with its document) or fails (with null).
  const uploadDocuments = async (
    files: File[],
    target: TUploadTarget,
    onSettled?: (file: File, uploaded: TMinFile | null) => void
  ): Promise<{ uploaded: TMinFile[]; failed: File[] }> => {
    setIsUploading(true);
    files.forEach((file) => addPending(file.name));
    const uploaded: TMinFile[] = [];
    const failed: File[] = [];

    await Promise.all(
      files.map(async (file) => {
        let minFile: TMinFile | null = null;
        try {
          const doc = await uploadDocumentFile(file, target);
          setUploadedDocuments((prev) => [...prev, doc]);
          minFile = {
            name: doc.fileName,
            size: file.size,
            id: doc.documentId,
            status: doc.status,
            isDeleting: false,
            isUploading: false
          };
          uploaded.push(minFile);
        } catch (error) {
          console.error(`Failed to upload ${file.name}:`, error);
          failed.push(file);
        } finally {
          removePending(file.name);
        }
        onSettled?.(file, minFile);
      })
    );
    setIsUploading(false);
    return { uploaded, failed };
  };

  const addWebLink = async (url: string, language?: string): Promise<TMinFile> => {
    setIsUploading(true);
    addPending(url);
    try {
      const doc = await preUploadWebLink(url, language);
      setUploadedDocuments((prev) => [...prev, doc]);

      const minFile: TMinFile = {
        name: doc.webUrl || url,
        size: 0,
        id: doc.documentId,
        status: doc.status,
        isDeleting: false,
        isUploading: false,
        webUrl: doc.webUrl || url
      };

      return minFile;
    } finally {
      removePending(url);
      setIsUploading(false);
    }
  };

  const addExternalDocument = (doc: TPreUploadedDocument) => {
    setUploadedDocuments((prev) => {
      if (prev.some((d) => d.documentId === doc.documentId)) return prev;
      return [...prev, doc];
    });
  };

  // With the draft's vector store, the file is also taken out of what its chat can search.
  const removeDocument = (documentId: number, target?: Pick<TUploadTarget, "vectorStoreId" | "organizationId">) => {
    setUploadedDocuments((prev) => prev.filter((doc) => doc.documentId !== documentId));
    $api
      .delete(`/projects/documents/pre-upload/${documentId}`, {
        params: {
          ...(target?.vectorStoreId && { vectorStoreId: target.vectorStoreId }),
          ...(target?.organizationId && { organizationId: target.organizationId })
        }
      })
      .catch((error) => {
        console.error(`Failed to delete pre-uploaded document ${documentId}:`, error);
      });
  };

  const clearDocuments = () => {
    setUploadedDocuments([]);
  };

  return {
    uploadedDocuments,
    isUploading,
    pendingUploadNames,
    uploadDocuments,
    addWebLink,
    addExternalDocument,
    removeDocument,
    clearDocuments
  };
};
